import * as React from 'react';

import Grid from '@material-ui/core/Grid';
import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { withOktaAuth } from '@okta/okta-react';
import { Form, FormikProps, withFormik } from 'formik';
import { withSnackbar, withSnackbarProps } from 'notistack';
import { getTranslate, Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { compose, setDisplayName } from 'recompose';

import { accountActions } from '../../actions/account.actions';
import { accountConstants } from '../../constants/account.constants';
import { maxLength, validate } from '../../helpers/validation';
import { accountRoutesConst as routes } from '../../routes/account.routes';
import CookiePreferences from './Common/CookiePreferences';
import { IntroButton } from './Common/IntroButton';
import AuthLayout from './Common/Layout';
import LoginTitle from './Login/LoginTitle';
import UnderlinedTextButton from './Onboarding/UnderlinedTextButton';

interface IFormValues {
  email: string;
}

const styles = (theme: Theme) =>
  createStyles({
    formFields: {
      // remove chrome autofill color
      '& input:-webkit-autofill': {
        boxShadow: '0 0 0 30px white inset !important',
      },
    },
    textField: {
      // prevent jumping screen
      minHeight: '68px',
    },
    newToFlipdish: {
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(0.5),
    },
    bottomContainer: {
      marginTop: 'auto',
      display: 'flex',
      padding: theme.spacing(2),
    },
  });

const Title = () => (
  <Translate>
    {(translate) => (
      <LoginTitle
        titlePrefix={translate('Welcome') as string}
        title={(translate('please_login') as string).toLowerCase()}
      />
    )}
  </Translate>
);

type MappedState = ReturnType<ReturnType<typeof mapStateToPropsFactory>>;
const mapStateToPropsFactory = () => {
  return (state: AppState) => {
    const { account } = state;
    return {
      account,
      translate: getTranslate(state.locale),
    };
  };
};

type Props = WithStyles<typeof styles> &
  MappedState &
  withSnackbarProps &
  RouteComponentProps & { dispatch: ThunkDispatch } & FormikProps<IFormValues>;
const Login = compose<Props, {}>(
  setDisplayName('Login'),
  withStyles(styles, {
    name: 'FdLogin',
    withTheme: true,
  }),
  withRouter,
  withSnackbar,
  connect(mapStateToPropsFactory),
  withFormik<Props, IFormValues>({
    displayName: 'LoginForm',
    mapPropsToValues: (props) => {
      return {
        email: props.account.Email || '',
      };
    },
    handleSubmit: async (values, { props, setSubmitting, setFieldError }) => {
      const { dispatch } = props;
      try {
        await dispatch(accountActions.loginRequestPin(values.email));
        setSubmitting(false);
      } catch (error) {
        setFieldError('email', props.translate('Nobody_registered_with_that_address') as string);
        setSubmitting(false);
        props.enqueueSnackbar(props.translate('Nobody_registered_with_that_address'), {
          variant: 'error',
        });
      }
    },
    validateOnBlur: false,
    isInitialValid: (props: Props) => {
      return props.account.Email && validate.email(props.account.Email) === undefined
        ? true
        : false;
    },
    validate: (values) => {
      return validate.email(values.email);
    },
  }),
  withOktaAuth
)(
  class extends React.Component<Props, {}> {
    private inputRef = React.createRef<HTMLInputElement>();

    public componentDidMount() {
      this.focus();
    }
    public componentDidUpdate(prevProps: Props) {
      if (prevProps.isSubmitting && !this.props.isSubmitting) {
        if (this.props.errors.email) {
          this.focus();
        } else {
          let nextRoute: string | null = null;
          if (this.props.oktaAuth && this.props.account.forceOktaLogin) {
            this.props.oktaAuth.signInWithRedirect({
              loginHint: this.props.account.Email || '',
            });
          } else if (this.props.account.pinSent) {
            nextRoute = routes.LoginPin;
          } else {
            nextRoute = routes.LoginPassword;
          }
          nextRoute && this.props.history.push(nextRoute);
        }
      }
    }

    public render() {
      const {
        account,
        classes,
        translate,
        isValid,
        isSubmitting,
        touched,
        errors,
        handleBlur,
        handleChange,
        values,
      } = this.props;

      return (
        <AuthLayout title={Title}>
          <Grid container alignItems="stretch" direction="column" wrap="nowrap" spacing={2}>
            <Grid item>
              <Form>
                <Grid container spacing={0} className={classes.formFields}>
                  <Grid item xs={12} lg={11}>
                    <TextField
                      inputRef={this.inputRef}
                      name="email"
                      label={translate('Email')}
                      placeholder={translate('Email') as string}
                      type="email"
                      autoComplete="email"
                      value={values.email}
                      fullWidth
                      inputProps={{
                        maxLength: maxLength.email,
                        'data-fd': 'email',
                      }}
                      className={classes.textField}
                      error={touched.email && !!errors.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText={touched.email && errors.email}
                    />
                  </Grid>
                  <Grid item xs={12} lg={11}>
                    <IntroButton
                      resource={accountConstants.LOGIN_REQUEST_PIN}
                      loading={isSubmitting || account.forceOktaLogin}
                      disabled={!isValid || isSubmitting || account.forceOktaLogin}
                      data-fd="next-button"
                    >
                      <Translate id={'Next'} />
                    </IntroButton>
                  </Grid>
                </Grid>
              </Form>
            </Grid>
            <Grid item container direction="column" className={classes.extras}>
              <Grid item>
                <Typography variant="caption">
                  <Translate id="By_logging_in_you_confirm_that_you_accept_the_Term" />
                </Typography>
              </Grid>
              <Grid item className={classes.newToFlipdish}>
                <Typography variant="subtitle1">
                  <Translate id="New_to_flipdish" />
                </Typography>
              </Grid>
              <Grid item>
                <UnderlinedTextButton fdKey="signup-here" to={routes.Signup}>
                  <Translate id="Signup_here" />
                </UnderlinedTextButton>
              </Grid>
            </Grid>
            <Grid item className={classes.bottomContainer}>
              <CookiePreferences />
            </Grid>
          </Grid>
        </AuthLayout>
      );
    }

    public focus = () => {
      if (this.inputRef.current) {
        this.inputRef.current.focus();
      }
    };
  }
);

export default Login;
